import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useParams, useLocation, useSearch } from "wouter";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { useAuth } from "@/lib/auth";
import { useTranslation } from "react-i18next";
import { format } from "date-fns";
import { type Session, type Class } from "@shared/schema";
import { Calendar, Clock, Play, ArrowLeft, Lock, Share2, Copy, Check } from "lucide-react";
import { LiveBroadcastIcon } from "@/components/live-broadcast-icon";
import { DEFAULT_THUMBNAIL } from "@/lib/constants";

interface SessionDetail {
  session: Session;
  class: Class;
  provider: { name: string };
  shareUrl?: string | null;
}

export default function SessionLobby() {
  const { sessionId } = useParams<{ sessionId: string }>();
  const { user } = useAuth();
  const [, navigate] = useLocation();
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);

  const { data, isLoading } = useQuery<SessionDetail>({
    queryKey: ["/api/sessions", sessionId, "state"],
  });

  if (isLoading) {
    return (
      <div className="mx-auto max-w-2xl px-4 py-12 sm:px-6">
        <Skeleton className="h-64 w-full rounded-xl" />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="mx-auto max-w-2xl px-4 py-16 text-center sm:px-6">
        <p className="text-muted-foreground">Session not found</p>
      </div>
    );
  }

  const { session, class: cls, provider, shareUrl } = data;
  const isLive = session.status === "LIVE";
  const isEnded = session.status === "ENDED";

  const handleCopyShareLink = async () => {
    if (!shareUrl) return;
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {}
  };

  return (
    <div className="mx-auto max-w-2xl px-4 py-8 sm:px-6">
      <div className="glass-page-bg" />
      <Button variant="ghost" size="sm" onClick={() => {
        const params = new URLSearchParams(window.location.search);
        navigate(params.get("from") === "calendar" ? "/calendar" : `/classes/${cls.id}`);
      }} className="mb-4" data-testid="button-back">
        <ArrowLeft className="mr-1 h-4 w-4" />
        {new URLSearchParams(window.location.search).get("from") === "calendar" ? t("nav.calendar") : "Back to Class"}
      </Button>

      <div className="glass-panel depth-shadow-lg overflow-hidden">
        <div className="relative">
          <img
            src={cls.thumbnailUrl || DEFAULT_THUMBNAIL}
            alt={cls.title}
            className="aspect-video w-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent" />
          <div className="absolute bottom-0 left-0 p-6">
            <Badge
              variant={isLive ? "default" : "secondary"}
              className={isLive ? "glass-badge-live no-default-hover-elevate" : "no-default-hover-elevate"}
            >
              {isLive && <LiveBroadcastIcon size={14} color="white" className="mr-1" />}
              {isLive ? "Live Now" : isEnded ? "Ended" : "Starting Soon"}
            </Badge>
            <h1 className="mt-2 text-xl font-bold text-white sm:text-2xl glow-text" data-testid="text-session-title">
              {cls.title}
            </h1>
            <p className="mt-1 text-sm text-white/70">by {provider.name}</p>
          </div>
        </div>

        <div className="p-6 space-y-4">
          <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground">
            <span className="flex items-center gap-1.5">
              <Calendar className="h-4 w-4" />
              {format(new Date(session.startsAt), "EEEE, MMMM d, yyyy")}
            </span>
            <span className="flex items-center gap-1.5">
              <Clock className="h-4 w-4" />
              {format(new Date(session.startsAt), "h:mm a")}
            </span>
          </div>

          <div className="flex flex-wrap gap-2">
            <Badge variant="outline" className="no-default-hover-elevate glass-input">Chat: {session.chatMode}</Badge>
            {session.allowReactions && <Badge variant="outline" className="no-default-hover-elevate glass-input">Reactions On</Badge>}
            <Badge variant="outline" className="no-default-hover-elevate glass-input">{session.visibility}</Badge>
          </div>

          {shareUrl && (
            <div className="flex items-center gap-2 p-3 glass-share-link" data-testid="share-link-lobby">
              <Share2 className="h-4 w-4 shrink-0 text-primary" />
              <code className="flex-1 truncate text-xs text-muted-foreground" data-testid="text-share-url-lobby">
                {shareUrl}
              </code>
              <Button
                size="sm"
                variant="outline"
                onClick={handleCopyShareLink}
                className="shrink-0 gap-1.5"
                data-testid="button-copy-share-lobby"
              >
                {copied ? <Check className="h-3.5 w-3.5 text-green-500" /> : <Copy className="h-3.5 w-3.5" />}
                {copied ? t("common.copied") : t("common.copyLink")}
              </Button>
            </div>
          )}

          {isLive && user && (
            <Button
              size="lg"
              className="w-full btn-gradient-capsule btn-cta-glow"
              onClick={() => navigate(`/live/${session.id}`)}
              data-testid="button-join-live"
            >
              <Play className="mr-2 h-4 w-4" />
              Join Live Session
            </Button>
          )}

          {isLive && !user && (
            <div className="space-y-3">
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Lock className="h-4 w-4" />
                Sign in to join this live session
              </div>
              <Button
                className="w-full btn-gradient-capsule btn-cta-glow"
                onClick={() => navigate("/login")}
                data-testid="button-signin-to-join"
              >
                Sign In
              </Button>
            </div>
          )}

          {isEnded && (
            <div className="glass-ended-panel p-4 text-center">
              <p className="text-sm text-muted-foreground">This session has ended</p>
            </div>
          )}

          {!isLive && !isEnded && (
            <div className="glass-ended-panel p-4 text-center">
              <p className="text-sm text-muted-foreground">
                This session hasn't started yet. Check back at the scheduled time.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
